/**
 * NotificationService - Browser Notification API wrapper
 *
 * Provides a singleton service for managing browser notifications with:
 * - Permission management (check, request)
 * - Notification lifecycle tracking
 * - Room-based notification grouping
 * - Cancellation support (single, by room, all)
 *
 * Usage:
 *   import notificationService from '../services/notificationService';
 *
 *   // Request permission
 *   const permission = await notificationService.requestPermission();
 *
 *   // Show notification
 *   notificationService.showNotification({
 *     eventId: 'evt123',
 *     roomId: '!room:server',
 *     title: 'New Message',
 *     body: 'Hello world',
 *     onClick: () => navigateToRoom(roomId),
 *   });
 *
 *   // Cancel notifications
 *   notificationService.cancelRoomNotifications(roomId);
 */

import logger from '../utils/logger';

class NotificationService {
  constructor() {
    // Map of eventId -> { notification: Notification, roomId: string }
    this.activeNotifications = new Map();

    // Check browser support
    this.isSupported = typeof window !== 'undefined' && 'Notification' in window;

    if (!this.isSupported) {
      logger.warn('NotificationService: Browser notifications not supported');
    }
  }

  /**
   * Check current notification permission status
   * @returns {'granted'|'denied'|'default'|'unsupported'} Current permission state
   */
  checkPermission() {
    if (!this.isSupported) {
      return 'unsupported';
    }
    return Notification.permission;
  }

  /**
   * Request notification permission from the user
   * @returns {Promise<'granted'|'denied'|'default'|'unsupported'>} Permission result
   */
  async requestPermission() {
    logger.debug('NotificationService: requestPermission() called');
    logger.debug(`NotificationService: isSupported=${this.isSupported}, current permission=${typeof Notification !== 'undefined' ? Notification.permission : 'N/A'}`);

    if (!this.isSupported) {
      logger.debug('NotificationService: Cannot request permission - notifications not supported');
      return 'unsupported';
    }

    // If already denied, we cannot re-request (browser restriction)
    if (Notification.permission === 'denied') {
      logger.debug('NotificationService: Permission already denied by user');
      return 'denied';
    }

    // If already granted, no need to request again
    if (Notification.permission === 'granted') {
      logger.debug('NotificationService: Permission already granted');
      return 'granted';
    }

    try {
      logger.debug('NotificationService: Calling Notification.requestPermission()...');
      const result = await Notification.requestPermission();
      logger.debug(`NotificationService: Permission request result: ${result}`);
      return result;
    } catch (error) {
      logger.error('NotificationService: Permission request failed:', error);
      return 'denied';
    }
  }

  /**
   * Show a browser notification
   * @param {Object} options - Notification options
   * @param {string} options.eventId - Unique identifier for the event (used for tracking/cancellation)
   * @param {string} options.roomId - Room ID for grouping notifications
   * @param {string} options.title - Notification title
   * @param {string} [options.body] - Notification body text (truncated to 100 chars)
   * @param {string} [options.icon] - URL for notification icon
   * @param {string} [options.tag] - Tag for notification grouping (defaults to eventId)
   * @param {Function} [options.onClick] - Callback when notification is clicked
   * @param {boolean} [options.playSound=false] - Whether to play notification sound
   * @returns {string|null} eventId if notification was created, null otherwise
   */
  showNotification({ eventId, roomId, title, body, icon, tag, onClick, playSound = false }) {
    // Validate required parameters
    if (!eventId || !roomId || !title) {
      logger.warn('NotificationService: Missing required parameters (eventId, roomId, title)');
      return null;
    }

    // Check if notifications are supported and permitted
    if (!this.isSupported) {
      logger.debug('NotificationService: Cannot show notification - not supported');
      return null;
    }

    // If permission not granted, check if we can request it
    if (Notification.permission !== 'granted') {
      if (Notification.permission === 'default') {
        // Permission hasn't been requested yet - request it now
        // Note: requestPermission is async but we return immediately
        // The notification won't show this time, but will on subsequent events
        this.requestPermission().then(result => {
          if (result === 'granted') {
            logger.info('NotificationService: Permission granted after prompt');
          }
        });
        logger.debug('NotificationService: Permission requested - notification deferred');
      } else {
        logger.debug('NotificationService: Cannot show notification - permission denied');
      }
      return null;
    }

    try {
      // Create the notification
      const notification = new Notification(title, {
        body: body ? body.substring(0, 100) : undefined,
        icon: icon || '/images/AppIcon60x60.png',
        tag: tag || eventId,
        silent: !playSound,
      });

      // Handle click event
      notification.onclick = () => {
        // Focus the window/tab
        window.focus();

        // Call the provided callback if any
        if (typeof onClick === 'function') {
          try {
            onClick();
          } catch (error) {
            logger.error('NotificationService: onClick callback error:', error);
          }
        }

        // Close the notification after click
        notification.close();
      };

      // Handle close event to clean up tracking
      notification.onclose = () => {
        this.activeNotifications.delete(eventId);
      };

      // Handle error event
      notification.onerror = (error) => {
        logger.error('NotificationService: Notification error:', error);
        this.activeNotifications.delete(eventId);
      };

      // Track the notification
      this.activeNotifications.set(eventId, { notification, roomId });

      logger.debug(`NotificationService: Created notification for event ${eventId} in room ${roomId}`);
      return eventId;
    } catch (error) {
      logger.error('NotificationService: Failed to create notification:', error);
      return null;
    }
  }

  /**
   * Cancel a specific notification by event ID
   * @param {string} eventId - The event ID of the notification to cancel
   */
  cancelNotification(eventId) {
    const entry = this.activeNotifications.get(eventId);
    if (entry) {
      try {
        entry.notification.close();
      } catch (error) {
        logger.debug('NotificationService: Error closing notification:', error);
      }
      this.activeNotifications.delete(eventId);
      logger.debug(`NotificationService: Cancelled notification ${eventId}`);
    }
  }

  /**
   * Cancel all notifications for a specific room
   * @param {string} roomId - The room ID whose notifications should be cancelled
   * @returns {number} Number of notifications cancelled
   */
  cancelRoomNotifications(roomId) {
    if (!roomId) {
      return 0;
    }

    let count = 0;
    for (const [eventId, entry] of this.activeNotifications) {
      if (entry.roomId === roomId) {
        try {
          entry.notification.close();
        } catch (error) {
          logger.debug('NotificationService: Error closing notification:', error);
        }
        this.activeNotifications.delete(eventId);
        count++;
      }
    }

    if (count > 0) {
      logger.debug(`NotificationService: Cancelled ${count} notifications for room ${roomId}`);
    }
    return count;
  }

  /**
   * Cancel all active notifications
   * @returns {number} Number of notifications cancelled
   */
  cancelAllNotifications() {
    const count = this.activeNotifications.size;

    for (const [, entry] of this.activeNotifications) {
      try {
        entry.notification.close();
      } catch (error) {
        logger.debug('NotificationService: Error closing notification:', error);
      }
    }

    this.activeNotifications.clear();

    if (count > 0) {
      logger.debug(`NotificationService: Cancelled all ${count} notifications`);
    }
    return count;
  }

  /**
   * Get the number of active notifications
   * @returns {number} Count of active notifications
   */
  getActiveCount() {
    return this.activeNotifications.size;
  }

  /**
   * Get active notification count for a specific room
   * @param {string} roomId - Room ID to count notifications for
   * @returns {number} Count of active notifications for the room
   */
  getRoomNotificationCount(roomId) {
    if (!roomId) {
      return 0;
    }

    let count = 0;
    for (const [, entry] of this.activeNotifications) {
      if (entry.roomId === roomId) {
        count++;
      }
    }
    return count;
  }

  /**
   * Check if a notification exists for a specific event
   * @param {string} eventId - Event ID to check
   * @returns {boolean} True if notification exists
   */
  hasNotification(eventId) {
    return this.activeNotifications.has(eventId);
  }
}

// Export singleton instance
export default new NotificationService();
